/**
 * W52 (2026-05-17) — WeeklyBriefCard
 *
 * Dashboard-Card: zeigt das aktuelle Weekly Strategic Brief (Sonntag-Cron).
 * Liest aus v5_weekly_strategic_briefs den letzten Eintrag.
 * Manual-Trigger via Refresh-Button.
 */

import { useEffect, useState } from 'react';
import { Newspaper, Calendar, AlertTriangle, CheckCircle2, RefreshCw, Mail, Quote } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { CARD, CARD_HEADER, CARD_HINT, SECTION_DIVIDER } from '@/lib/design-tokens';

interface EarningsEvent {
  symbol: string;
  date: string;
  expected_eps?: number | null;
}

interface MacroEvent {
  event: string;
  date: string;
  impact?: string;
}

interface PositionToWatch {
  symbol: string;
  reason: string;
}

interface WeeklyBrief {
  iso_week: string;
  week_start: string;
  headline: string;
  macro_outlook: string;
  top_picks_summary: string | null;
  earnings_week: EarningsEvent[];
  macro_events_week: MacroEvent[];
  positions_to_watch: PositionToWatch[];
  recommended_focus: string | null;
  what_to_avoid: string | null;
  closing_thought: string | null;
  email_sent_at: string | null;
  generated_at: string;
  cost_usd?: number | null;
  search_queries?: number | null;
}

export function WeeklyBriefCard() {
  const [brief, setBrief] = useState<WeeklyBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadLatest();
  }, []);

  async function loadLatest() {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('v5_weekly_strategic_briefs')
        .select('*')
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) setBrief(data as WeeklyBrief);
    } finally {
      setLoading(false);
    }
  }

  async function refresh() {
    if (refreshing) return;
    setRefreshing(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/generate-weekly-brief?force=1&no_mail=1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        return;
      }
      const json = (await res.json()) as WeeklyBrief & { ok?: boolean };
      setBrief(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRefreshing(false);
    }
  }

  if (loading) {
    return (
      <div className={CARD}>
        <div className="text-xs text-zinc-500">Lade Weekly Brief...</div>
      </div>
    );
  }

  if (!brief) {
    return (
      <div className={CARD}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className={CARD_HEADER}>
              <Newspaper className="size-4 text-blue-400" />
              Weekly Strategic Brief
            </h3>
            <p className={cn(CARD_HINT, 'mt-1')}>
              Sonntag-KI-Wochenvorschau mit Macro-Outlook, Top-Picks, Earnings + Macro-Events.
              Wird Sonntags 18:00 UTC automatisch generiert + per E-Mail versendet.
            </p>
          </div>
          <button
            onClick={() => void refresh()}
            disabled={refreshing}
            className="text-xs px-3 py-1.5 rounded-md bg-blue-500/20 border border-blue-500/40 text-blue-300 hover:bg-blue-500/30 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            {refreshing ? 'Generiere...' : 'Jetzt erstellen'}
          </button>
        </div>
        {error && <div className="mt-2 text-xs text-red-300">{error}</div>}
      </div>
    );
  }

  return (
    <div className="rounded-lg border-2 border-blue-500/40 bg-blue-950/20 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
            <Newspaper className="size-4 text-blue-400" />
            Weekly Strategic Brief
          </h3>
          <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2">
            <span>KW {brief.iso_week} · ab {brief.week_start}</span>
            {brief.email_sent_at && (
              <span className="flex items-center gap-1 text-emerald-400">
                <Mail className="size-3" />
                versendet
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => void refresh()}
          disabled={refreshing}
          className="text-xs p-1.5 rounded border border-zinc-700/60 hover:bg-zinc-800/60 disabled:opacity-50"
          title="Neu generieren"
        >
          <RefreshCw className={cn('size-3.5', refreshing && 'animate-spin')} />
        </button>
      </div>

      {/* Headline */}
      <div className="rounded-md border-l-4 border-blue-500 bg-blue-950/40 px-3 py-2">
        <p className="text-sm text-zinc-100 leading-snug font-medium">{brief.headline}</p>
      </div>

      {/* Macro-Outlook */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">
          Macro-Outlook
        </div>
        <p className="text-xs text-zinc-300 leading-relaxed">{brief.macro_outlook}</p>
      </div>

      {/* Top Picks */}
      {brief.top_picks_summary && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">
            Top-Picks der Woche
          </div>
          <p className="text-xs text-zinc-200 leading-relaxed">{brief.top_picks_summary}</p>
        </div>
      )}

      {/* Earnings + Macro Events Side-by-Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {brief.earnings_week.length > 0 && (
          <div className="rounded-md border border-amber-500/30 bg-amber-950/20 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-400 mb-1.5 flex items-center gap-1.5">
              <Calendar className="size-3" />
              Earnings diese Woche
            </div>
            <ul className="space-y-1">
              {brief.earnings_week.map((e, i) => (
                <li key={i} className="text-[11px] text-zinc-200 flex items-baseline gap-2">
                  <span className="font-mono text-amber-300">{e.symbol}</span>
                  <span className="text-zinc-500">{e.date}</span>
                  {e.expected_eps != null && (
                    <span className="text-zinc-600">~${e.expected_eps.toFixed(2)} EPS</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {brief.macro_events_week.length > 0 && (
          <div className="rounded-md border border-purple-500/30 bg-purple-950/20 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-purple-400 mb-1.5 flex items-center gap-1.5">
              <Calendar className="size-3" />
              Macro-Events
            </div>
            <ul className="space-y-1">
              {brief.macro_events_week.map((e, i) => (
                <li key={i} className="text-[11px] text-zinc-200 leading-snug">
                  <span className="font-mono text-purple-300">{e.date}</span>: {e.event}
                  {e.impact && <span className="text-zinc-500"> ({e.impact})</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Positions to Watch */}
      {brief.positions_to_watch.length > 0 && (
        <div className="rounded-md border border-zinc-700/50 bg-zinc-900/50 px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">
            Positionen mit Aufmerksamkeit
          </div>
          <ul className="space-y-1">
            {brief.positions_to_watch.map((p, i) => (
              <li key={i} className="text-[11px] text-zinc-200">
                <span className="font-mono text-zinc-100">{p.symbol}</span>: {p.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Focus + Avoid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {brief.recommended_focus && (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-950/20 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400 mb-1.5 flex items-center gap-1.5">
              <CheckCircle2 className="size-3" />
              Diese Woche fokussieren
            </div>
            <p className="text-[11px] text-zinc-200 leading-snug">{brief.recommended_focus}</p>
          </div>
        )}
        {brief.what_to_avoid && (
          <div className="rounded-md border border-red-500/30 bg-red-950/20 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-red-400 mb-1.5 flex items-center gap-1.5">
              <AlertTriangle className="size-3" />
              Vermeiden
            </div>
            <p className="text-[11px] text-zinc-200 leading-snug">{brief.what_to_avoid}</p>
          </div>
        )}
      </div>

      {/* Closing */}
      {brief.closing_thought && (
        <div className="rounded-md border border-zinc-800/60 bg-zinc-950/40 px-3 py-2 flex items-start gap-2">
          <Quote className="size-3.5 text-zinc-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-zinc-400 italic leading-snug">{brief.closing_thought}</p>
        </div>
      )}

      {/* Footer */}
      <div className={cn('text-[10px] text-zinc-600 flex items-center justify-between pt-2', SECTION_DIVIDER)}>
        <span>
          Generiert {new Date(brief.generated_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
          {brief.search_queries != null && brief.search_queries > 0 && ` · ${brief.search_queries} Web-Suchen`}
        </span>
        {brief.cost_usd != null && brief.cost_usd > 0 && (
          <span>~${brief.cost_usd.toFixed(4)}</span>
        )}
      </div>
    </div>
  );
}
